import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { color } from '@/colors';
import { FlexColCenHor } from '../styles';
import * as Icons from './index';

const iconCategories = {
  // General UI Icons
  general: [
    { name: 'CheckMarkOutlineIcon', Component: Icons.CheckMarkOutlineIcon },
    { name: 'XCircleOutlineIcon', Component: Icons.XCircleOutlineIcon },
    { name: 'CheckMarkIcon', Component: Icons.CheckMarkIcon },
    { name: 'CheckMarkCircleIcon', Component: Icons.CheckMarkCircleIcon },
    { name: 'InfoIcon', Component: Icons.InfoIcon },
    { name: 'ArrowIcon', Component: Icons.ArrowIcon },
    { name: 'WarningCircleIcon', Component: Icons.WarningCircleIcon },
    { name: 'WarningTriangleIcon', Component: Icons.WarningTriangleIcon },
    { name: 'XIcon', Component: Icons.XIcon },
    { name: 'QuestionMarkIcon', Component: Icons.QuestionMarkIcon },
    { name: 'MinusIcon', Component: Icons.MinusIcon },
    { name: 'PlusIcon', Component: Icons.PlusIcon },
    { name: 'DotsThreeIcon', Component: Icons.DotsThreeIcon },
    { name: 'ThreeDotsVerticalIcon', Component: Icons.ThreeDotsVerticalIcon },
    { name: 'AngleIcon', Component: Icons.AngleIcon },
    { name: 'AsteriskIcon', Component: Icons.AsteriskIcon },
    { name: 'CaretIcon', Component: Icons.CaretIcon },
  ],

  // App Navigation & Actions
  navigation: [
    { name: 'UndoIcon', Component: Icons.UndoIcon },
    { name: 'RedoIcon', Component: Icons.RedoIcon },
    { name: 'UploadIcon', Component: Icons.UploadIcon },
    { name: 'DownloadIcon', Component: Icons.DownloadIcon },
    { name: 'SaveIcon', Component: Icons.SaveIcon },
    { name: 'SearchIcon', Component: Icons.SearchIcon },
    { name: 'ShareIcon', Component: Icons.ShareIcon },
    { name: 'ToggleIcon', Component: Icons.ToggleIcon },
    { name: 'SortIcon', Component: Icons.SortIcon },
    { name: 'NavigateIcon', Component: Icons.NavigateIcon },
    { name: 'RecentIcon', Component: Icons.RecentIcon },
    { name: 'ReRunIcon', Component: Icons.ReRunIcon },
    { name: 'MaximizeArrowsIcon', Component: Icons.MaximizeArrowsIcon },
    { name: 'SingleViewModeIcon', Component: Icons.SingleViewModeIcon },
    { name: 'AllViewModeIcon', Component: Icons.AllViewModeIcon },
  ],

  // User & Team Management
  users: [
    { name: 'UsersIcon', Component: Icons.UsersIcon },
    { name: 'TeamIcon', Component: Icons.TeamIcon },
    { name: 'SmileyXEyes', Component: Icons.SmileyXEyes },
  ],

  // Content & Media
  content: [
    { name: 'TextIcon', Component: Icons.TextIcon },
    { name: 'TextSlashIcon', Component: Icons.TextSlashIcon },
    { name: 'ImageIcon', Component: Icons.ImageIcon },
    { name: 'ImageSlashIcon', Component: Icons.ImageSlashIcon },
    { name: 'AudioIcon', Component: Icons.AudioIcon },
    { name: 'MediaIcon', Component: Icons.MediaIcon },
    { name: 'VideoCameraIcon', Component: Icons.VideoCameraIcon },
    { name: 'VideoCameraLockIcon', Component: Icons.VideoCameraLockIcon },
    { name: 'VideoSmooterIcon', Component: Icons.VideoSmooterIcon },
    { name: 'VideoUpscaleIcon', Component: Icons.VideoUpscaleIcon },
    { name: 'VideoModelsIcon', Component: Icons.VideoModelsIcon },
    { name: 'PlayFilledIcon', Component: Icons.PlayFilledIcon },
    { name: 'PlayOutlineIcon', Component: Icons.PlayOutlineIcon },
    { name: 'PauseFilledIcon', Component: Icons.PauseFilledIcon },
  ],

  // AI & Models
  models: [
    { name: 'AnyLLMIcon', Component: Icons.AnyLLMIcon },
    { name: 'ImageDescriberIcon', Component: Icons.ImageDescriberIcon },
    { name: 'ImageModelsIcon', Component: Icons.ImageModelsIcon },
    { name: 'ThreeDIcon', Component: Icons.ThreeDIcon },
    { name: 'ThreeDModelsIcon', Component: Icons.ThreeDModelsIcon },
    { name: 'SparkleIcon', Component: Icons.SparkleIcon },
    { name: 'SparkleSlashIcon', Component: Icons.SparkleSlashIcon },
  ],

  // Image Processing Tools
  imageTools: [
    { name: 'ExtractVideoFrameIcon', Component: Icons.ExtractVideoFrameIcon },
    { name: 'InvertIcon', Component: Icons.InvertIcon },
    { name: 'ChannelsIcon', Component: Icons.ChannelsIcon },
    { name: 'MaskExtractionIcon', Component: Icons.MaskExtractionIcon },
    { name: 'MasksIcon', Component: Icons.MasksIcon },
    { name: 'MergeAlphaIcon', Component: Icons.MergeAlphaIcon },
    { name: 'BlurIcon', Component: Icons.BlurIcon },
    { name: 'CropIcon', Component: Icons.CropIcon },
    { name: 'ResizeIcon', Component: Icons.ResizeIcon },
    { name: 'RelightIcon', Component: Icons.RelightIcon },
    { name: 'EyeDropIcon', Component: Icons.EyeDropIcon },
    { name: 'FiltersIcon', Component: Icons.FiltersIcon },
    { name: 'PainterIcon', Component: Icons.PainterIcon },
    { name: 'PencilLineIcon', Component: Icons.PencilLineIcon },
  ],

  // AI Prompts & Generation
  prompts: [
    { name: 'PromptIcon', Component: Icons.PromptIcon },
    { name: 'PromptConcatIcon', Component: Icons.PromptConcatIcon },
    { name: 'PromptEnhanceIcon', Component: Icons.PromptEnhanceIcon },
    { name: 'SeedIcon', Component: Icons.SeedIcon },
  ],

  // Interface & Controls
  interface: [
    { name: 'AppWindowIcon', Component: Icons.AppWindowIcon },
    { name: 'Cards3Icon', Component: Icons.Cards3Icon },
    { name: 'CompositorIcon', Component: Icons.CompositorIcon },
    { name: 'ToolboxIcon', Component: Icons.ToolboxIcon },
    { name: 'SlidersIcon', Component: Icons.SlidersIcon },
    { name: 'StickyNoteIcon', Component: Icons.StickyNoteIcon },
    { name: 'ListIcon', Component: Icons.ListIcon },
    { name: 'NumberIcon', Component: Icons.NumberIcon },
    { name: 'RouterIcon', Component: Icons.RouterIcon },
    { name: 'TextIteratorIcon', Component: Icons.TextIteratorIcon },
    { name: 'ImageIteratorIcon', Component: Icons.ImageIteratorIcon },
    { name: 'VideoIteratorIcon', Component: Icons.VideoIteratorIcon },
  ],

  // Data & I/O
  data: [
    { name: 'ImportIcon', Component: Icons.ImportIcon },
    { name: 'ExportIcon', Component: Icons.ExportIcon },
    { name: 'CopyIcon', Component: Icons.CopyIcon },
    { name: 'OutputIcon', Component: Icons.OutputIcon },
    { name: 'SetAsInputIcon', Component: Icons.SetAsInputIcon },
    { name: 'RevertAsInputIcon', Component: Icons.RevertAsInputIcon },
  ],

  // Links & Connections
  connections: [
    { name: 'LinkIcon', Component: Icons.LinkIcon },
    { name: 'LinkBreakIcon', Component: Icons.LinkBreakIcon },
  ],

  // Security & Access
  security: [
    { name: 'LockClosedIcon', Component: Icons.LockClosedIcon },
    { name: 'KeyIcon', Component: Icons.KeyIcon },
    { name: 'LockKeyIcon', Component: Icons.LockKeyIcon },
    { name: 'LockOpenIcon', Component: Icons.LockOpenIcon },
  ],

  // Visual Elements
  visual: [
    { name: 'EyeIcon', Component: Icons.EyeIcon },
    { name: 'HandIcon', Component: Icons.HandIcon },
    { name: 'GlobeIcon', Component: Icons.GlobeIcon },
    { name: 'GiftIcon', Component: Icons.GiftIcon },
    { name: 'MacCmdIcon', Component: Icons.MacCmdIcon },
  ],

  // Brand Logos
  brands: [
    { name: 'WeavyLogoFilled', Component: Icons.WeavyLogoFilled },
    { name: 'WeavyTextLogo', Component: Icons.WeavyTextLogo },
    { name: 'XTwitterLogo', Component: Icons.XTwitterLogo },
    { name: 'YouTubeLogo', Component: Icons.YouTubeLogo },
    { name: 'LinkedinLogo', Component: Icons.LinkedinLogo },
    { name: 'DiscordLogo', Component: Icons.DiscordLogo },
    { name: 'GoogleLogo', Component: Icons.GoogleLogo },
    { name: 'MicrosoftLogo', Component: Icons.MicrosoftLogo },
    { name: 'YarnIcon', Component: Icons.YarnIcon },
  ],

  // 3D & Advanced
  advanced: [
    { name: 'ThreeDRotateIcon', Component: Icons.ThreeDRotateIcon },
    { name: 'ThreeDRotateSlashIcon', Component: Icons.ThreeDRotateSlashIcon },
  ],

  video: [{ name: 'CanvasIcon', Component: Icons.CanvasIcon }],
};
const categoryLabels = {
  general: 'General UI',
  navigation: 'Navigation & Actions',
  users: 'User & Team',
  content: 'Content & Media',
  models: 'AI & Models',
  imageTools: 'Image Processing',
  prompts: 'AI Prompts',
  interface: 'Interface & Controls',
  data: 'Data & I/O',
  connections: 'Links & Connections',
  security: 'Security & Access',
  visual: 'Visual Elements',
  services: 'External Services',
  brands: 'Brand Logos',
  advanced: '3D & Advanced',
  video: 'Video',
};

const IconsPreview = () => {
  const [copiedName, setCopiedName] = useState<string | null>(null);

  const handleCopy = (name: string) => {
    void navigator.clipboard.writeText(name);
    setCopiedName(name);
    setTimeout(() => setCopiedName(null), 1200);
  };

  const totalIcons = Object.values(iconCategories).flat().length;

  return (
    <>
      <Box sx={{ p: 4, bgcolor: color.Black92 }}>
        {Object.entries(iconCategories).map(([categoryKey, icons]) => (
          <Box key={categoryKey} sx={{ mb: 2, border: '1px solid #fff', p: 1 }}>
            <Typography variant="h3">
              {categoryLabels[categoryKey as keyof typeof categoryLabels]} ({icons.length})
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Grid2 container spacing={4}>
                {icons.map(({ name, Component }) => (
                  <Grid2 size={{ xs: 2, sm: 1 }} key={name} sx={{ textAlign: 'center' }}>
                    <FlexColCenHor
                      sx={{ borderRadius: 1, cursor: 'pointer', ':hover': { bgcolor: color.White04_T } }}
                      onClick={() => handleCopy(name)}
                    >
                      <Component style={{ fontSize: 32, width: 32, height: 32 }} />
                      <Typography sx={{ mt: 1, wordBreak: 'break-all' }}>
                        {copiedName === name ? 'Copied!' : name.replace(/Icon$/, '')}
                      </Typography>
                    </FlexColCenHor>
                  </Grid2>
                ))}
              </Grid2>
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: color.Black92 }}>
        <Typography sx={{ color: color.White64_T, fontSize: '0.875rem' }}>Total Icons: {totalIcons}</Typography>
      </Box>
    </>
  );
};

export default IconsPreview;
